import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { usePageTitle } from "../hooks/usePageTitle";
import { useLanguage } from "../lib/LanguageContext";
import Breadcrumb from "../components/Breadcrumb";
import CannabisLeaf from "@/components/CannabisLeaf";

export default function About() {
  const { language } = useLanguage();
  const isPl = language === "pl";

  usePageTitle(
    isPl ? "O portalu — TerpeneAtlas" : "About — TerpeneAtlas",
    isPl
      ? "Dowiedz się więcej o TerpeneAtlas — bazie wiedzy o terpenach i odmianach medycznej marihuany dostępnych w Polsce."
      : "Learn more about TerpeneAtlas — a knowledge base about terpenes and medical cannabis strains available in Poland."
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {isPl ? "Powrót do strony głównej" : "Back to Home"}
      </Link>

      <Breadcrumb
        items={[
          { label: isPl ? "Strona główna" : "Home", to: "/" },
          { label: isPl ? "O portalu" : "About" },
        ]}
      />

      <div className="flex items-center justify-center gap-3 mb-6">
        <CannabisLeaf className="w-8 h-8 text-primary" />
        <h1 className="text-3xl md:text-4xl font-playfair font-bold">
          {isPl ? "O portalu" : "About TerpeneAtlas"}
        </h1>
      </div>

      <div className="bg-card rounded-xl border border-border p-6 space-y-4 text-sm text-muted-foreground leading-relaxed">
        {isPl ? (
          <>
            <p>
              TerpeneAtlas pomaga Pacjentom i Lekarzom zrozumieć medyczne konopie, od działania poszczególnych
              terpenów po to, jak funkcjonuje cała roślina. Znajdziesz tu szczegółowe informacje o odmianach
              dostępnych w Polsce oraz przystępne wyjaśnienia najważniejszych zagadnień terapii kannabinoidowej,
              bez konieczności przebijania się przez skomplikowaną literaturę naukową.
            </p>
            <p>
              Wybieranie odmiany wyłącznie na podstawie zawartości THC lub oznaczenia indica/sativa przypomina wybór
              wina tylko według zawartości alkoholu. Terpeny nadają każdej odmianie charakterystyczny aromat, a nauka
              coraz bardziej interesuje się tym, jak wpływają na organizm. Do najlepiej udokumentowanych właściwości
              terpenów należy działanie przeciwzapalne, uspokajające i przeciwlękowe, nasenne, przeciwbólowe oraz
              antyoksydacyjne. Choć większość tych badań jest wciąż na wczesnym etapie, znajomość profilu
              terpenowego odmiany pozwala pełniej zrozumieć działanie konopi.
            </p>
            <p>
              Na TerpeneAtlas znajdziesz szczegółowe karty odmian z ich profilami terpenowymi, narzędzie do porównywania
              produktów oraz encyklopedię terpenów opisującą właściwości każdego z nich. Na stronie każdej odmiany
              zebrano komentarze Pacjentów oraz podsumowanie opinii z innych źródeł, wraz z odsetkiem opinii
              pozytywnych. Przewodnik Pacjenta wyjaśnia podstawy terapii konopnej z odniesieniami do literatury
              naukowej, a sekcja Konopie w badaniach oferuje obszerną bazę publikacji naukowych. Słowniczek w przystępny sposób wyjaśnia kluczowe
              pojęcia związane z terapią konopną. Portal jest regularnie aktualizowany wraz z pojawianiem się nowych
              produktów na rynku.
            </p>
            <p>
              Niektóre materiały graficzne na stronie, jak np. kalkulator temperatury waporyzacji czy symbole
              poszczególnych terpenów w „Encyklopedii terpenów", zostały wygenerowane za pomocą sztucznej inteligencji.
            </p>
            <p>
              Sekcje „Doświadczenia Pacjentów" na stronach odmian opierają się wyłącznie na prawdziwych, publicznie
              dostępnych komentarzach w języku polskim. Źródła obejmują grupy na Facebooku poświęcone terapii
              konopnej oraz strony takie jak wykop.pl, reddit.com, budcare.pl, odmianymarihuany.pl i konopneforum.pl.
              Podsumowania tworzą modele sztucznej inteligencji, które obliczają też odsetek komentarzy pozytywnych
              i negatywnych. Sekcje są aktualizowane w sposób automatyczny, wraz z pojawianiem się nowych komentarzy w sieci.
            </p>
            <p>
              Administrator strony zaznacza, że nie
              tworzy samodzielnie treści zawartych w tych sekcjach i nie ma możliwości ręcznego wpływania na ich
              zawartość, procent pozytywnych/negatywnych opinii wyświetlany na stronie każdej z odmian, faworyzowanie
              lub celowe obniżanie oceny poszczególnych produktów. Jest to tylko i wyłącznie wynik działania
              konkretnego algorytmu i treści zawartych w w/w źródłach. Analogicznie, w sposób zautomatyzowany
              wybierana jest "Odmiana tygodnia".
            </p>
            <p>
              Przed opublikowaniem profili terpenowych leków recepturowych zawierających fragmenty rośliny <em>Cannabis sativa</em> zwrócono się do każdego z Producentów z prośbą o weryfikację składu terpenowego ich produktów. Odpowiedzi udzieliła jedynie znacząca mniejszość — w związku z tym większość danych dotyczących procentowego udziału terpenów w poszczególnych odmianach pochodzi z publicznie dostępnych źródeł i należy traktować je jako orientacyjne.
            </p>
            <p>
              Dołożono wszelkich starań, aby prezentowane informacje były jak najbardziej rzetelne. Jeśli dysponujesz dokładniejszymi danymi, uprzejmie proszę o ich przesłanie na adres admin@terpeneatlas.org lub skorzystanie z zakładki{" "}
              <Link to="/kontakt" className="text-foreground underline hover:text-primary">Kontakt</Link>.
            </p>
            <p>
              TerpeneAtlas został stworzony przez Lekarkę z pasją do edukacji medycznej i chęcią wspierania Pacjentów
              oraz Lekarzy w codziennej pracy z coraz bogatszą ofertą medycznej marihuany w Polsce. Mam nadzieję, że
              stanie się Twoim przewodnikiem — zarówno przy wyborze nowej odmiany, jak i w rozmowie z Lekarzem
              prowadzącym lub wsparciem w prowadzeniu Pacjentów w terapii konopnej.
            </p>
          </>
        ) : (
          <>
            <p>
              TerpeneAtlas helps patients and physicians understand medical cannabis, from the effects of individual
              terpenes to how the plant itself works. Here you'll find detailed information on the strains available
              in Poland, along with straightforward explanations of the key aspects of cannabinoid therapy, all
              without the need to dig through complex scientific literature.
            </p>
            <p>
              Choosing your cannabis solely on its THC content, or an indica/sativa label, is a bit like choosing
              wine only by its alcohol percentage. Terpenes give each strain its distinctive aroma, and science is
              increasingly interested in how they affect the body. The best-documented properties of terpenes
              include anti-inflammatory, calming and anti-anxiety, sedative, pain-relieving and antioxidant effects.
              While most of this research is still at an early stage, understanding a strain's terpene profile can
              give you a more complete picture of what you are choosing.
            </p>
            <p>
              On TerpeneAtlas you will find detailed strain profiles with their terpene composition, a tool for
              comparing products side by side, and a terpene encyclopedia describing the properties of each compound.
              Each strain page brings together patient comments and a summary of opinions gathered from other
              sources, including the share of positive reviews. The Patient Guide explains the basics of cannabis
              therapy with references to scientific literature, and the Cannabis Research section offers an extensive
              database of publications on the use of medical cannabis across a wide range of conditions. A glossary
              explains key terms related to cannabis therapy in plain language. The portal
              is regularly updated as new products appear on the market.
            </p>
            <p>
              Some graphics on the site, including the vaporization temperature calculator and the terpene symbols in
              the Encyclopedia, were created with the help of artificial intelligence.
            </p>
            <p>
              The "Patient Experiences" sections on strain pages are based only on real, publicly available
              Polish-language comments. Sources include Facebook groups on cannabis therapy and sites such as
              wykop.pl, reddit.com, budcare.pl, odmianymarihuany.pl and konopneforum.pl. AI models write the
              summaries and calculate the share of positive and negative comments. These sections are being
              updated automatically as new opinions appear online.
            </p>
            <p>
              The site's administrator does not write these sections and cannot manually change their content or
              the share of positive and negative opinions shown for each strain. No product is promoted or
              downgraded by hand. The results come entirely from an automated process and the content of the
              sources listed above. The "Strain of the Week" is also selected automatically in a similar way.
            </p>
            <p>
              Before publishing terpene profiles of cannabis strains available in Polish pharmacies,
              I asked every producer to verify the terpene composition of their products. Only a few responded, so
              most of the terpene percentages shown for individual strains come from publicly available sources and
              should be treated as approximate.
            </p>
            <p>
              I do my best to keep all information accurate and up to date. If you have more precise data or
              spot an error, please email me at admin@terpeneatlas.org or use the{" "}
              <Link to="/kontakt" className="text-foreground underline hover:text-primary">Contact</Link> page.
            </p>
            <p>
              I am a physician passionate about medical education, and I created TerpeneAtlas to support patients
              and physicians as they navigate the growing range of medical cannabis products available in Poland.
              I hope it serves as a helpful guide, whether you are choosing a new strain, preparing for a
              conversation with your doctor, or guiding your own patients through cannabis therapy.
            </p>
          </>
        )}
      </div>
    </div>
  );
}